import { useState, useCallback, useEffect } from 'react';
import { Box, Accordion, SimpleGrid, Text } from '@chakra-ui/react';

import ReadOnlyCollapsibleCard from '../components/ReadOnlyCollapsibleCard';
import {
  LocationCard,
  ProtectionClassCard,
  EvaluationToolsCard,
  CoverageAmountsCard,
} from './general';
import { useAnswerCodes, useLockAwareAccordion } from '../hooks';
import type { Location, GeneralData } from 'api';
import { formatAddress, getRiskAddress } from '../constants/locationOptions';

interface GeneralStepProps {
  data: GeneralData;
  quoteId: number;
  onUpdate: (locations: Location[]) => void;
  onSave?: () => Promise<void>;
  isUpdating?: boolean;
}

const GeneralStep = ({
  data,
  quoteId,
  onUpdate,
  onSave,
  isUpdating,
}: GeneralStepProps) => {
  const { getLabel } = useAnswerCodes();
  const [locations, setLocations] = useState<Location[]>(data.locations || []);

  useEffect(() => {
    setLocations(data.locations || []);
  }, [data.locations]);

  const handleLocationFieldUpdate = useCallback(
    (index: number, field: keyof Location, value: unknown) => {
      setLocations((prevLocations) => {
        const updatedLocations = prevLocations.map((loc, i) =>
          i === index ? { ...loc, [field]: value } : loc,
        );
        onUpdate(updatedLocations);
        return updatedLocations;
      });
    },
    [onUpdate],
  );

  const [expandedIndex, setExpandedIndex] = useState<number>(0);
  const accordionProps = useLockAwareAccordion({
    itemCount: locations.length,
    expandedIndex,
    setExpandedIndex,
  });

  const getLocationTitle = (location: Location) => {
    const riskType = location.location_type
      ? getLabel(location.location_type, 'location_type')
      : '';
    const fullAddress = formatAddress(getRiskAddress(location));

    if (riskType && fullAddress) {
      return `${riskType} – ${fullAddress}`;
    }
    return riskType || fullAddress || 'New Location';
  };

  if (locations.length === 0) {
    return (
      <Box p={8} textAlign="center">
        <Text color="gray.500">
          No locations found. Please add locations in the Locations step first.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Accordion {...accordionProps}>
        {locations.map((location, index) => (
          <ReadOnlyCollapsibleCard
            key={location.id}
            title={getLocationTitle(location)}
          >
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
              <LocationCard location={location} />

              <EvaluationToolsCard
                location={location}
                locationIndex={index}
                quoteId={quoteId}
                onUpdate={handleLocationFieldUpdate}
                onSave={onSave}
                isUpdating={isUpdating}
              />

              <ProtectionClassCard
                location={location}
                locationIndex={index}
                onUpdate={handleLocationFieldUpdate}
                isUpdating={isUpdating}
              />

              <CoverageAmountsCard
                location={location}
                locationIndex={index}
                onUpdate={handleLocationFieldUpdate}
                isUpdating={isUpdating}
              />
            </SimpleGrid>
          </ReadOnlyCollapsibleCard>
        ))}
      </Accordion>
    </Box>
  );
};

export default GeneralStep;
